import { fetchBillingProducts, fetchMarketPricing } from '@/api/resources/billing.api';
import { Components } from '@/api/generated/api';
import { useAuth } from '@/auth/useAuth';
import { formatAmount, getProductMarketPricing, getProductMeta, productOrder, ProductWithMeta } from '@/utils/billing';
import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQueryWithToast } from '../useQueryWithToast';
import { isNotNullish } from 'common/utils/array.utils';
import { useCurrentTenant } from '../schoolAdmin/useCurrentTenant';
import { billingKeys } from './billing.keys';

export interface IBillingProductsComposable {
  allBillingProductsWithMeta: ComputedRef<ProductWithMeta[]>;
}

/**
 * FAIL-OPEN policy around {@link fetchMarketPricing}, shared by the tenant billing page and the
 * onboarding checkout: a null result simply hides purchase surfaces — a pricing hiccup must never
 * block the page or the onboarding.
 */
export async function fetchMarketPricingFailOpen(
  countryId?: number,
): Promise<Components.Schemas.BillingMarketPricingDtoData | null> {
  try {
    return await fetchMarketPricing(countryId);
  } catch {
    return null;
  }
}

export function useBillingProducts(enabled: MaybeRefOrGetter<boolean> = true): IBillingProductsComposable {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();
  const { currentTenant } = useCurrentTenant();

  function enrichProduct(
    p: Components.Schemas.ProductTypeDto,
    marketPricing: Components.Schemas.BillingMarketPricingDtoData | null,
  ): ProductWithMeta | null {
    const meta = getProductMeta(p.product);
    if (!meta) return null;

    const pricing = getProductMarketPricing(p, marketPricing);
    const priceLabel = pricing
      ? t(meta.pricingLabelKey, {
          price: formatAmount(pricing.yearly?.amountCents ?? 0, pricing.currency, {
            alwaysShowCents: true,
            spaced: true,
          }),
          perSeat: pricing.yearly?.perSeatCents
            ? formatAmount(pricing.yearly.perSeatCents, pricing.currency, { alwaysShowCents: true, spaced: true })
            : undefined,
          serviceFee: formatAmount(pricing.yearly?.amountCents ?? 0, pricing.currency, {
            alwaysShowCents: true,
            spaced: true,
          }),
        })
      : '';
    return { ...p, meta, pricing, priceLabel };
  }

  function sortProducts(enriched: ProductWithMeta[]): ProductWithMeta[] {
    return enriched.sort((a, b) => productOrder.indexOf(a.product) - productOrder.indexOf(b.product));
  }

  const query = useQueryWithToast({
    queryKey: billingKeys.products(),
    queryFn: fetchBillingProducts,
    enabled: computed(() => toValue(enabled)),
  });

  const marketPricingQueryKey = computed(() => billingKeys.pricing(currentTenant.value?.id ?? null));
  const marketPricingQuery = useQueryWithToast({
    queryKey: marketPricingQueryKey,
    queryFn: () => fetchMarketPricingFailOpen(),
    enabled: computed(() => toValue(enabled) && isLoggedIn.value && currentTenant.value != null),
  });

  const allBillingProducts = computed<Components.Schemas.ProductTypeDto[]>(() => {
    return query.data.value ?? [];
  });

  const allBillingProductsWithMeta = computed<ProductWithMeta[]>(() => {
    const marketPricing = marketPricingQuery.data.value ?? null;
    const enriched = allBillingProducts.value.map((p) => enrichProduct(p, marketPricing)).filter(isNotNullish);

    return sortProducts(enriched);
  });

  return {
    allBillingProductsWithMeta,
  };
}
